import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import { movies } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Detail() {
  const { id } = useParams();
  const movie = movies.find((item) => item.id === id);
  const [toast, setToast] = useState("");
  const [showPlayer, setShowPlayer] = useState(false);

  usePageMeta({
    title: movie
      ? `色戒完整版无删减hd-电影详情·${movie.title}`
      : "色戒完整版无删减hd-电影详情",
    description: movie
      ? `${movie.title}（${movie.year}）影片资料：导演 ${movie.director}，主演 ${movie.cast
          .map((person) => person.name)
          .join("、")}。${movie.summary}`
      : "该影片资料不存在或已下架，可返回影视分类页浏览其他高清影片资料。",
    keywords: movie
      ? `${movie.title},${movie.title}剧情,${movie.genres.join(",")},${movie.director},高清电影,色戒完整版无删减hd`
      : "电影详情,影片资料,高清电影",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    document.body.style.overflow = showPlayer ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showPlayer]);

  const related = useMemo(() => {
    if (!movie) return [];
    const scored = movies
      .filter((item) => item.id !== movie.id)
      .map((item) => {
        const sharedGenres = item.genres.filter((genre) => movie.genres.includes(genre)).length;
        const sharedTags = item.tags.filter((tag) => movie.tags.includes(tag)).length;
        return { item, score: sharedGenres * 2 + sharedTags * 3 + (item.type === movie.type ? 1 : 0) };
      })
      .sort((a, b) => b.score - a.score);
    return scored.slice(0, 8).map((entry) => entry.item);
  }, [movie]);

  if (!movie) {
    return (
      <PageShell>
        <div className="mx-auto max-w-shell px-4 py-16 md:px-6 md:py-24">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "电影详情" }]} />
          <div className="mt-8">
            <EmptyState
              icon="ri-error-warning-line"
              title="内容不存在"
              description="你访问的影片可能已经下架，或者链接中的编号有误。可以返回分类页重新浏览演示影片库。"
              actionLabel="返回影视分类"
              onAction={() => {
                window.REACT_APP_NAVIGATE?.("/category");
              }}
              secondaryLabel="回到首页"
              onSecondary={() => {
                window.REACT_APP_NAVIGATE?.("/");
              }}
            />
          </div>
        </div>
      </PageShell>
    );
  }

  const metaRows = [
    { label: "上映年份", value: `${movie.year} 年` },
    { label: "内容类型", value: movie.type },
    { label: "影片地区", value: movie.region },
    { label: "片长", value: movie.duration },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.map((person) => person.name).join(" / ") },
  ];

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <PageShell>
      <section className="relative overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={movie.cover}
            alt=""
            aria-hidden="true"
            className="h-full w-full scale-110 object-top opacity-30 blur-2xl"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/95 to-primary-950/70" />
        </div>

        <div className="relative mx-auto max-w-shell px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb
            tone="dark"
            items={[
              { label: "首页", to: "/" },
              { label: "影视分类", to: "/category" },
              { label: `电影详情·${movie.title}` },
            ]}
          />

          <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-12">
            <div className="md:col-span-3 lg:col-span-2">
              <div className="mx-auto w-[160px] overflow-hidden rounded-lg border border-background-50/20 sm:w-[190px] md:mx-0 md:w-full">
                <img
                  src={movie.cover}
                  alt={`${movie.title} ${movie.year} 高清封面`}
                  title={`${movie.title} · ${movie.genres.join(" ")} 影片封面`}
                  className="aspect-[2/3] w-full object-top"
                />
              </div>
            </div>

            <div className="md:col-span-9 lg:col-span-10">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2 py-0.5 text-[11px] font-medium text-background-50">
                  高清修复
                </span>
                <span className="rounded-md border border-background-50/25 px-2 py-0.5 text-[11px] text-background-200">
                  {movie.type}
                </span>
                {movie.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md border border-background-50/25 px-2 py-0.5 text-[11px] text-background-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="mt-4 font-heading text-[26px] font-black leading-tight text-background-50 md:text-[36px]">
                {movie.title}
              </h1>
              <p className="mt-1 font-label text-[12px] uppercase tracking-[0.18em] text-background-400">
                {movie.subtitle}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-background-200">
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-400" />
                  <span className="font-label text-sm font-semibold text-accent-400">
                    {movie.rating.toFixed(1)}
                  </span>
                  / 10
                </span>
                <span className="h-3 w-px bg-background-50/25" />
                <span>{movie.year}</span>
                <span className="h-3 w-px bg-background-50/25" />
                <span>{movie.genres.join(" / ")}</span>
                <span className="h-3 w-px bg-background-50/25" />
                <span>{movie.duration}</span>
                <span className="h-3 w-px bg-background-50/25" />
                <span>导演 {movie.director}</span>
              </div>

              <p className="mt-5 max-w-3xl text-sm leading-[1.9] text-background-200">{movie.summary}</p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowPlayer(true)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-fill text-lg" />
                  播放预告（演示）
                </button>
                <button
                  type="button"
                  onClick={copyLink}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-4 py-2.5 text-sm text-background-50 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  <i className="ri-link" />
                  复制链接
                </button>
                <Link
                  to="/category"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-4 py-2.5 text-sm text-background-50 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  <i className="ri-arrow-left-line" />
                  返回分类
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-shell px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="min-w-0 lg:col-span-8">
            <section>
              <div className="mb-4 flex items-center gap-2">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                {movie.synopsis.split("\n\n").map((paragraph, index) => (
                  <p
                    key={index}
                    className="mb-4 text-sm leading-[1.95] text-foreground-700 last:mb-0 md:text-[15px]"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            <section className="mt-10">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="h-4 w-1 rounded-full bg-accent-500" />
                  <h2 className="font-heading text-lg font-bold text-foreground-950">演员资料</h2>
                </div>
                <span className="text-[11px] text-foreground-400">共 {movie.cast.length} 位主创</span>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {movie.cast.map((person) => (
                  <div
                    key={person.name}
                    className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                  >
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full bg-background-200">
                      <img
                        src={person.avatar}
                        alt={`${person.name} 演员头像`}
                        title={`${person.name} · ${person.role}`}
                        className="h-full w-full object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <h3 className="font-heading text-sm font-semibold text-foreground-950">
                          {person.name}
                        </h3>
                        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] text-secondary-900">
                          {person.role}
                        </span>
                      </div>
                      <p className="mt-1.5 text-[11px] leading-relaxed text-foreground-600">
                        {person.bio}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-10">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="h-4 w-1 rounded-full bg-accent-500" />
                  <h2 className="font-heading text-lg font-bold text-foreground-950">相关推荐</h2>
                </div>
                <span className="hidden items-center gap-1 text-[11px] text-foreground-400 sm:flex">
                  <i className="ri-drag-move-2-line" />
                  左右滑动查看更多
                </span>
              </div>

              <div className="no-scrollbar -mx-4 flex gap-3 overflow-x-auto px-4 pb-2 md:mx-0 md:px-0">
                {related.map((item) => (
                  <div key={item.id} className="w-[140px] shrink-0 md:w-[150px]">
                    <MovieCard movie={item} />
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-[88px] space-y-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                  <i className="ri-information-line text-accent-600" />
                  影片信息
                </h2>
                <dl className="mt-4 space-y-3">
                  {metaRows.map((row) => (
                    <div key={row.label} className="flex gap-3 text-xs">
                      <dt className="w-16 shrink-0 text-foreground-400">{row.label}</dt>
                      <dd className="flex-1 leading-relaxed text-foreground-800">{row.value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-5 border-t border-background-200 pt-4">
                  <p className="mb-2 text-[11px] text-foreground-400">题材标签</p>
                  <div className="flex flex-wrap gap-1.5">
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-accent-200 bg-accent-50 px-2.5 py-0.5 text-[11px] text-accent-800"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                  <i className="ri-share-forward-line text-accent-600" />
                  分享这部影片
                </h3>
                <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">
                  分享功能为前端演示，会复制当前页面地址，社交平台按钮不会产生真实分享数据。
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={copyLink}
                    className="flex cursor-pointer flex-col items-center gap-1 rounded-md border border-background-300 bg-background-50 py-2.5 text-[11px] text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600"
                  >
                    <i className="ri-file-copy-line text-base" />
                    复制
                  </button>
                  <button
                    type="button"
                    onClick={() => setToast("已生成分享卡片（演示）")}
                    className="flex cursor-pointer flex-col items-center gap-1 rounded-md border border-background-300 bg-background-50 py-2.5 text-[11px] text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600"
                  >
                    <i className="ri-wechat-line text-base" />
                    微信
                  </button>
                  <button
                    type="button"
                    onClick={() => setToast("已打开分享窗口（演示）")}
                    className="flex cursor-pointer flex-col items-center gap-1 rounded-md border border-background-300 bg-background-50 py-2.5 text-[11px] text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600"
                  >
                    <i className="ri-weibo-line text-base" />
                    微博
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {showPlayer && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-primary-950/80 px-4 backdrop-blur-sm">
          <div className="animate-fade-in w-full max-w-lg rounded-lg border border-background-300 bg-background-50 p-6">
            <div className="flex items-start justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <i className="ri-movie-2-line text-xl" />
              </span>
              <button
                type="button"
                onClick={() => setShowPlayer(false)}
                aria-label="关闭播放提示"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-200"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
              播放功能为演示占位
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              本站仅展示影片资料、剧情与演员信息，不提供《{movie.title}》的在线播放或下载。播放按钮用于演示页面交互，
              点击后不会加载任何视频内容。
            </p>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setShowPlayer(false)}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                继续看资料
              </button>
              <Link
                to="/news"
                className="flex-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-center text-sm text-foreground-700 transition-colors hover:bg-background-100"
              >
                看相关资讯
              </Link>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="animate-fade-in fixed bottom-6 left-1/2 z-[80] -translate-x-1/2">
          <div className="flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-xs text-background-50">
            <i className="ri-checkbox-circle-fill text-accent-400" />
            {toast}
          </div>
        </div>
      )}
    </PageShell>
  );
}